<script setup lang="ts">
// The one icon component of the web. Hand-drawn stroke icons ported from the production front end
// (assets/mobile-ui/shell.js `paths`, assets/js/app.js `ICONS`, the landing feature icons); path data is copied
// unchanged. "repeat" and "play" are drawn here in the same style: production shows emoji glyphs on those spots.
//
// variant "client": phone tab bar form (class client-icon, stroke attributes on the svg, as shell.js icon()).
// variant "bare": only the viewBox; the surrounding legacy CSS sets size and stroke (sidebar, landing features).
// variant "line" (default): self-contained stroke icon for new code.
import { computed } from 'vue'

export type IconName =
  | 'home' | 'clipboard' | 'checklist' | 'mic' | 'microphone' | 'pin' | 'map' | 'profile' | 'user' | 'crown'
  | 'document' | 'list-check' | 'columns' | 'chat' | 'video' | 'repeat' | 'play' | 'arrow-right' | 'arrow-left'
  | 'calendar' | 'book' | 'image' | 'cloud'

// Each entry is the list of svg child elements (markup), exactly as in the legacy source.
const SHAPES: Record<IconName, string> = {
  home: '<path d="m3 11 9-8 9 8"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/>',
  clipboard: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h3"/>',
  checklist: '<path d="M9 5h10M9 12h10M9 19h10"/><path d="m3.5 5 1.2 1.2L7 3.8M3.5 12l1.2 1.2L7 10.8M3.5 19l1.2 1.2L7 17.8"/>',
  mic: '<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/>',
  microphone: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8"/>',
  pin: '<path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z"/><circle cx="12" cy="10" r="2.5"/>',
  map: '<path d="m3 6 5-2 8 3 5-2v13l-5 2-8-3-5 2zM8 4v13M16 7v13"/><circle cx="12" cy="11" r="2"/>',
  profile: '<circle cx="12" cy="7" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4.5 21a7.5 7.5 0 0 1 15 0"/>',
  crown: '<path d="m3 7 4 4 5-7 5 7 4-4-2 13H5zM6 16h12"/>',
  document: '<path d="M6 3h12v18H6zM9 7h6M9 11h6M9 15h4"/>',
  'list-check': '<path d="M4 5h16M4 12h16M4 19h10"/><path d="m4 5 1 1 2-2M4 12l1 1 2-2"/>',
  columns: '<path d="M4 4h7v16H4zM13 4h7v16h-7zM4 8h7M13 8h7"/>',
  chat: '<path d="M4 5h16v11H8l-4 4z"/><path d="M8 9h8M8 12h5"/>',
  video: '<rect x="3" y="5" width="14" height="14" rx="2"/><path d="m17 9 4-2v10l-4-2z"/>',
  repeat: '<path d="M17 2l4 4-4 4"/><path d="M3 11V9a3 3 0 0 1 3-3h15"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v2a3 3 0 0 1-3 3H3"/>',
  play: '<path d="M8 5v14l11-7z"/>',
  'arrow-right': '<path d="m9 5 7 7-7 7"/>',
  'arrow-left': '<path d="m15 19-7-7 7-7"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v5M17 3v5M3 11h18M7 15h2M13 15h3"/>',
  book: '<path d="M12 6v15M3 4h5a4 4 0 0 1 4 3 4 4 0 0 1 4-3h5v15h-5a4 4 0 0 0-4 2 4 4 0 0 0-4-2H3z"/>',
  image: '<rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8" cy="8" r="1.5"/><path d="m3 17 6-6 4 4 3-3 5 5"/>',
  cloud: '<path d="M7 18a5 5 0 1 1 0-10 6 6 0 0 1 12 1 4.5 4.5 0 0 1-1 9H7Z"/>',
}

const props = withDefaults(defineProps<{
  name: IconName
  variant?: 'line' | 'client' | 'bare'
  size?: number | string
  stroke?: number | string
}>(), { variant: 'line', size: 24, stroke: 1.65 })

const shapes = computed(() => SHAPES[props.name])
const stroked = computed(() => props.variant !== 'bare')
const sized = computed(() => props.variant === 'line')
</script>

<template>
  <svg :class="variant === 'client' ? 'client-icon' : variant === 'line' ? 'app-icon' : undefined" viewBox="0 0 24 24"
    :width="sized ? size : undefined" :height="sized ? size : undefined"
    :fill="stroked ? 'none' : undefined" :stroke="stroked ? 'currentColor' : undefined" :stroke-width="stroked ? stroke : undefined"
    :stroke-linecap="stroked ? 'round' : undefined" :stroke-linejoin="stroked ? 'round' : undefined" aria-hidden="true" v-html="shapes" />
</template>
<style>
.app-icon { flex: none; display: inline-block; vertical-align: middle; }
</style>
